import { CheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

export default function ProjectProgressBar({ currentStepIndex, setCurrentStepIndex, completedSteps = [] }) {
  const { t } = useTranslation()
  const [internalStepIndex, setInternalStepIndex] = useState(1) // 0-based index, starting with "Question" (index 1)
  
  // Use prop if provided, otherwise use internal state
  const activeStepIndex = currentStepIndex !== undefined ? currentStepIndex : internalStepIndex
  const setActiveStepIndex = setCurrentStepIndex || setInternalStepIndex

  const allSteps = [
    { id: '01', name: t('Projects.ProgressSteps.Prepare'), description: t('Projects.ProgressSteps.PrepareDescription') },
    { id: '02', name: t('Projects.ProgressSteps.Question'), description: t('Projects.ProgressSteps.QuestionDescription') },
    { id: '03', name: t('Projects.ProgressSteps.Analyze'), description: t('Projects.ProgressSteps.AnalyzeDescription') },
    { id: '04', name: t('Projects.ProgressSteps.Report'), description: t('Projects.ProgressSteps.ReportDescription') },
  ]

  // Generate steps with dynamic status based on completion tracking
  const steps = allSteps.map((step, index) => ({
    ...step,
    href: '#',
    status: completedSteps.includes(index) ? 'complete' : index === activeStepIndex ? 'current' : 'upcoming',
    completed: completedSteps.includes(index)
  }))

  const handleStepClick = (stepIndex) => {
    setActiveStepIndex(stepIndex)
  }

  return (
    <div className="mb-1">
      <nav aria-label="Progress">
        <ol role="list" className="divide-y divide-gray-300 rounded-md border border-gray-300 md:flex md:divide-y-0">
          {steps.map((step, stepIdx) => (
            <li key={step.name} className="relative md:flex md:flex-1">
              {step.status === 'complete' ? (
                <button onClick={() => handleStepClick(stepIdx)} data-testid={`flow-step-${stepIdx}`} className="group flex w-full items-center">
                  <span className="flex items-center px-6 py-4 text-sm font-medium">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-iavisus-100 group-hover:bg-iavisus-200">
                      <CheckIcon aria-hidden="true" className="size-6 text-white" />
                    </span>
                    <span className="ml-4 flex flex-col text-left">
                      <span className="text-base font-bold text-gray-900">{step.name}</span>
                      <span className="text-sm text-gray-500">{step.description}</span>
                    </span>
                  </span>
                </button>
              ) : step.status === 'current' ? (
                <button onClick={() => handleStepClick(stepIdx)} data-testid={`flow-step-${stepIdx}`} aria-current="step" className="flex items-center px-6 py-4 text-sm font-medium">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-iavisus-900">
                    <span className="text-white">{step.id}</span>
                  </span>
                  <span className="ml-4 flex flex-col text-left">
                    <span className="text-base font-bold text-iavisus-100">{step.name}</span>
                    <span className="text-sm text-iavisus-200">{step.description}</span>
                  </span>
                </button>
              ) : (
                <button onClick={() => handleStepClick(stepIdx)} data-testid={`flow-step-${stepIdx}`} className="group flex items-center">
                  <span className="flex items-center px-6 py-4 text-sm font-medium">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-hover:border-gray-400">
                      <span className="text-gray-500 group-hover:text-gray-900">{step.id}</span>
                    </span>
                    <span className="ml-4 flex flex-col text-left">
                      <span className="text-base font-bold text-gray-500 group-hover:text-gray-900">{step.name}</span>
                      <span className="text-sm text-gray-500 group-hover:text-gray-700">{step.description}</span>
                    </span>
                  </span>
                </button>
              )}

              {stepIdx !== steps.length - 1 ? (
                <>
                  {/* Arrow separator for md screens and up */}
                  <div aria-hidden="true" className="absolute right-0 top-0 hidden h-full w-5 md:block">
                    <svg fill="none" viewBox="0 0 22 80" preserveAspectRatio="none" className="size-full text-gray-300">
                      <path
                        d="M0 -2L20 40L0 82"
                        stroke="currentcolor"
                        vectorEffect="non-scaling-stroke"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </>
              ) : null}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  )
}
